//src/components/CreatorDetails.jsx
import React, { useEffect, useState } from "react";
import API from "../services/api";
import "./CreatorDetails.css";
import CreatorHistoryTimeline from "../components/CreatorHistoryTimeline";

// Simple icon replacements
const X = () => <span>✕</span>;
const ExternalLink = () => <span>🔗</span>;
const Users = () => <span>👥</span>;
const Eye = () => <span>👁️</span>;
const Heart = () => <span>❤️</span>;
const MessageCircle = () => <span>💬</span>;
const Calendar = () => <span>📅</span>;
const Clock = () => <span>🕒</span>;
const PenSquare = () => <span>✏️</span>;
const Trash2 = () => <span>🗑️</span>;
const Star = () => <span>⭐</span>;

export default function CreatorDetails({ creator, onClose, onEdit, onDelete }) {
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    if (creator) {
      API.get(`/analysis/creator_insights/${creator.id}`)
        .then(res =>{
          console.log("Insights response:", res.data); // Debug log 
          setInsights(res.data);
        })
        .catch(console.error);
    }
  }, [creator]);
  if (!creator) return null;

  const confirmDelete = () => {
    if (window.confirm(`Delete ${creator.creator_name}? This cannot be undone.`)) {
      onDelete(creator.id);
    }
  };

  return (
    <>
      {/* Background overlay */}
      <div className="overlay" onClick={onClose} />

      {/* Side panel */}
      <div className="details-panel">
        {/* Header */}
        <div className="details-header">
          <div>
            <h2>{creator.creator_name}</h2>
            <div className="tags">
              <span className="platform">{creator.platform}</span>
              <span className="niche">{creator.niche}</span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}><X /></button>
        </div>

        {/* Content */}
        <div className="details-content">
          <section>
            <h3>Creator Profile</h3>
            <a href={creator.creator_url} target="_blank" rel="noreferrer" className="profile-link">
              Visit Creator Profile <ExternalLink />
            </a>
          </section>

          <section>
            <h3>Performance</h3>
            <div className="stats-grid">
              <StatCard icon={<Users />} title="Followers" value={creator.followers?.toLocaleString()} />
              <StatCard icon={<Eye />} title="Average Views" value={creator.avg_views?.toLocaleString()} />
              <StatCard icon={<Heart />} title="Average Likes" value={creator.avg_likes?.toLocaleString()} />
              <StatCard icon={<MessageCircle />} title="Average Comments" value={creator.avg_comments?.toLocaleString()} />
            </div>
          </section>

          <section>
            <h3>Posting Insights</h3>
            <InfoRow icon={<Calendar />} label="Posting Frequency" value={`${creator.posting_frequency || 0} posts/week`} />
            <InfoRow icon={<Calendar />} label="Best Posting Day" value={creator.best_posting_day || "-"} />
            <InfoRow icon={<Clock />} label="Best Posting Time" value={creator.best_posting_time || "-"} />
          </section>

          <section>
            <h3>Top Performing Hook</h3>
            <div className="box">{creator.top_hook || "No hook recorded."}</div>
          </section>

          <section>
            <h3>Research Notes</h3>
            <div className="box">{creator.notes || "No notes available."}</div>
          </section>

          <section>
            <h3>Brand Fit Score</h3>
            <div className="score"><Star /> {creator.score}/100</div>
          </section>

          {insights && (
            <section>
              <h3>Intelligence Insights</h3>
              <p>Alpha Score: {insights.alpha_score}/100</p>
              <div className="insight-cards">
                <div className="insight-card">
                  <h4>Momentum</h4>
                  <p>{insights.metrics.momentum}</p>
                  <small>Views relative to followers — traction strength.</small>
                </div>
                <div className="insight-card">
                  <h4>Velocity</h4>
                  <p>{insights.metrics.velocity}</p>
                  <small>Follower base vs posting frequency — growth speed.</small>
                </div>
                <div className="insight-card">
                  <h4>Virality</h4>
                  <p>{insights.metrics.virality}</p>
                  <small>Likes relative to views —  breakout potentials.</small>
                </div>
                <div className="insight-card">
                  <h4>Consistency</h4>
                  <p>{insights.metrics.consistency}</p>
                  <small>Posting regularity compared to ideal schedule.</small>
                </div>
              </div>
            </section>
          )}

          {/* ✅ Embed history timeline */}
          <section>
            <CreatorHistoryTimeline creatorId={creator.id} />
          </section>

          <section className="actions">
            <button className="edit-btn" onClick={() => onEdit(creator)}><PenSquare /> Edit Creator</button>
            <button className="delete-btn" onClick={confirmDelete}><Trash2 /> Delete</button>
          </section>
        </div>
      </div>
    </>
  );
}

function StatCard({ icon, title, value }) {
  return (
    <div className="stat-card">
      <div className="stat-header">{icon}<span>{title}</span></div>
      <div className="stat-value">{value || "-"}</div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="info-row">
      <div className="info-icon">{icon}</div>
      <div>
        <div className="info-label">{label}</div>
        <div className="info-value">{value}</div>
      </div>
    </div>
  );
}
